import { Client } from '@/pages/user/api';
import { AccountModule } from '@/store/modules/account';
import * as Cache from '@/utils/cache.util';

const apiPrefix = 'portal';

export type ScCatalogItemType = 'SC_SKU' | 'SC_SYNC_PRODUCT' | 'PS_PRODUCT';

export type ScCatalogSizePrice = {
  sizeOptionSn?: string;
  sizeName?: string;
  unitPrice: number | string;
  sequence?: number;
};

export type ScCatalogItemBase = {
  itemType: ScCatalogItemType;
  sourceId: string | number;
  targetSn: string;
  name: string;
  imageUrl: string;
  supplierEnterpriseSn: string;
  supplierName: string;
  firstCategoryCode: string;
  firstCategoryName: string;
  secondCategoryCode: string;
  secondCategoryName: string;
  createdAt: string;
  favorited: boolean;
};

export type ScSkuCatalogItem = ScCatalogItemBase & {
  itemType: 'SC_SKU';
  description?: string;
  sizeText?: string;
  materialText?: string;
  craftText?: string;
  jdUrl?: string;
  referencePrice?: string;
};

export type ScSyncProductCatalogItem = ScCatalogItemBase & {
  itemType: 'SC_SYNC_PRODUCT';
  studioName?: string;
  proofNotice?: string;
  minQuantity?: number;
  stepQuantity?: number;
  sizePriceList: ScCatalogSizePrice[];
};

export type ScCatalogItem = ScSkuCatalogItem | ScSyncProductCatalogItem;

export type ScCatalogPageQuery = {
  firstCategoryCode: string;
  secondCategoryCode?: string;
  pageNum?: number;
  pageSize?: number;
};

export type ScCatalogFavoritePayload = {
  targetType: ScCatalogItemType;
  targetSn: string;
};

export type PsProductFavoritePayload = {
  productSn: string;
};

const parseFileNameFromDisposition = (disposition: string | null) => {
  if (!disposition) {
    return '';
  }
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    return decodeURIComponent(utf8Match[1]);
  }
  const normalMatch = disposition.match(/filename="?([^"]+)"?/i);
  if (normalMatch && normalMatch[1]) {
    return decodeURIComponent(normalMatch[1]);
  }
  return '';
};

const readBlobAsText = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob, 'utf-8');
  });

export const GetScTagList = async (): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/web/sc/tag/list',
  });
};

export const GetScSupplierPage = async (data: any): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/web/sc/supplier/page',
    data,
  });
};

export const GetScSkuCategoryAbilityList = async (): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/web/sc/sku/category/ability/list',
  });
};

export const GetScSupplierPartnerList = async (limit = 12): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/web/sc/supplier/partner/list',
    data: { limit },
  });
};

export const GetScSupplierDetail = async (data: any): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/web/sc/supplier/detail',
    data,
  });
};

export const GetScSupplierSkuPage = async (data: any): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/web/sc/sku/page',
    data,
  });
};

export const GetScCatalogPage = async (data: ScCatalogPageQuery): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/web/sc/catalog/page',
    data,
  });
};

export const CreateScCatalogFavorite = async (data: ScCatalogFavoritePayload): Promise<any | null> => {
  return await Client.post<any>({
    url: apiPrefix + '/sc/catalog/favorite',
    data,
  });
};

export const CancelScCatalogFavorite = async (data: ScCatalogFavoritePayload): Promise<any | null> => {
  return await Client.delete<any>({
    url: apiPrefix + '/sc/catalog/favorite',
    data,
    sendDataInBody: true,
  });
};

export const CreatePsProductFavorite = async (data: PsProductFavoritePayload): Promise<any | null> => {
  return await Client.post<any>({
    url: apiPrefix + '/ps/product/favorite',
    data,
  });
};

export const CancelPsProductFavorite = async (data: PsProductFavoritePayload): Promise<any | null> => {
  return await Client.delete<any>({
    url: apiPrefix + '/ps/product/favorite',
    data,
    sendDataInBody: true,
  });
};

export const GetPsProductFavoriteStatus = async (productSnList: string[]): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/ps/product/favorite/status',
    data: {
      productSnList: productSnList.join(','),
    },
  });
};

export const CreateScSkuFavorite = async (data: any): Promise<any | null> => {
  return await Client.post<any>({
    url: apiPrefix + '/sc/sku/favorite',
    data,
  });
};

export const CancelScSkuFavorite = async (data: any): Promise<any | null> => {
  return await Client.delete<any>({
    url: apiPrefix + '/sc/sku/favorite',
    data,
  });
};

export const GetMyScSkuFavoritePage = async (data: any): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/sc/sku/favorite/my/page',
    data,
  });
};

export const GetPsSupplierProductFavoriteStats = async (data: any): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/ps/supplier/product/favorite/stats',
    data,
  });
};

export const GetPsSupplierProductFavoritePage = async (data: any): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/ps/supplier/product/favorite/page',
    data,
  });
};

export const ExportPsSupplierProductFavorite = (data: any = {}) => {
  const params = new URLSearchParams();
  appendFavoriteExportParams(params, data);
  const requestUrl = `/portal/ps/supplier/product/favorite/export${params.toString() ? `?${params.toString()}` : ''}`;
  return downloadFavoriteExport(requestUrl, 'print-media-product-favorite-export.xlsx');
};

export const GetCurrentScSupplierDetail = async (): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/sc/supplier/detail',
  });
};

export const HasSavedCurrentScSupplierEnterpriseInfo = async (): Promise<boolean> => {
  try {
    const detail = await GetCurrentScSupplierDetail();
    if (!detail) {
      return false;
    }

    const hasSupplierSpecificText = Boolean((detail.supplierIntro || '').trim() || (detail.supplierMainProducts || '').trim());
    const hasSupplierSpecificSelection =
      (Array.isArray(detail.selectedTagCodeList) && detail.selectedTagCodeList.length > 0) ||
      (Array.isArray(detail.deviceList) && detail.deviceList.length > 0);

    return hasSupplierSpecificText || hasSupplierSpecificSelection;
  } catch (error) {
    return false;
  }
};

export const UpdateCurrentScSupplier = async (data: any): Promise<any | null> => {
  return await Client.put<any>({
    url: apiPrefix + '/sc/supplier/update',
    data,
  });
};

export const PublishCurrentScSupplier = async (): Promise<any | null> => {
  return await Client.post<any>({
    url: apiPrefix + '/sc/supplier/publish',
  });
};

export const GetCurrentScSupplierSkuList = async (): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/sc/supplier/sku/list',
  });
};

export const GetScSupplierSkuApprovalPage = async (data: any): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/sc/admin/sku/pageQuery',
    data,
  });
};

export const UpdateScSupplierSkuApprovalStatus = async (data: any): Promise<any | null> => {
  const { firstCategoryCode, secondCategoryCode, ...payload } = data || {};
  const params = new URLSearchParams();
  if (firstCategoryCode) {
    params.append('firstCategoryCode', firstCategoryCode);
  }
  if (secondCategoryCode) {
    params.append('secondCategoryCode', secondCategoryCode);
  }
  const queryString = params.toString();
  return await Client.put<any>({
    url: apiPrefix + `/sc/admin/sku/approvalStatus${queryString ? `?${queryString}` : ''}`,
    data: payload,
  });
};

export const ExportScSupplierSkuApproval = (data: {
  keyword?: string;
  supplierName?: string;
  approvalStatus?: number;
  firstCategoryCode?: string;
  secondCategoryCode?: string;
}) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const params = new URLSearchParams();
    if (data.keyword) {
      params.append('keyword', data.keyword);
    }
    if (data.supplierName) {
      params.append('supplierName', data.supplierName);
    }
    if (data.approvalStatus !== undefined && data.approvalStatus !== null) {
      params.append('approvalStatus', String(data.approvalStatus));
    }
    if (data.firstCategoryCode) {
      params.append('firstCategoryCode', data.firstCategoryCode);
    }
    if (data.secondCategoryCode) {
      params.append('secondCategoryCode', data.secondCategoryCode);
    }

    const requestUrl = `/portal/sc/admin/sku/export${params.toString() ? `?${params.toString()}` : ''}`;
    xhr.open('GET', requestUrl, true);
    xhr.responseType = 'blob';

    const token = AccountModule.token || Cache.getToken('user');
    if (token) {
      xhr.setRequestHeader('Authorization', token);
    }

    xhr.onload = async () => {
      const contentType = xhr.getResponseHeader('Content-Type') || '';
      if (
        xhr.status >= 200 &&
        xhr.status < 300 &&
        (contentType.indexOf('application/vnd') > -1 ||
          contentType.indexOf('application/octet-stream') > -1 ||
          contentType.indexOf('application/zip') > -1)
      ) {
        const fileName = parseFileNameFromDisposition(xhr.getResponseHeader('Content-Disposition')) || 'sku-approval-export.xlsx';
        const blobUrl = window.URL.createObjectURL(xhr.response);
        const link = document.createElement('a');
        link.style.display = 'none';
        link.href = blobUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        resolve();
        return;
      }

      try {
        const text = await readBlobAsText(xhr.response);
        const res = JSON.parse(text);
        reject(new Error((res && (res.msg || res.message)) || '下载失败'));
      } catch (error) {
        reject(new Error('下载失败'));
      }
    };

    xhr.onerror = () => reject(new Error('网络异常'));
    xhr.send();
  });

const appendFavoriteExportParams = (params: URLSearchParams, data: any = {}) => {
  ['keyword', 'supplierEnterpriseSn', 'firstCategoryCode', 'secondCategoryCode', 'startDate', 'endDate', 'sortType'].forEach(key => {
    if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
      params.append(key, String(data[key]));
    }
  });
};

const downloadFavoriteExport = (requestUrl: string, defaultFileName: string) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', requestUrl, true);
    xhr.responseType = 'blob';

    const token = AccountModule.token || Cache.getToken('user');
    if (token) {
      xhr.setRequestHeader('Authorization', token);
    }

    xhr.onload = async () => {
      const contentType = xhr.getResponseHeader('Content-Type') || '';
      if (
        xhr.status >= 200 &&
        xhr.status < 300 &&
        (contentType.indexOf('application/vnd') > -1 ||
          contentType.indexOf('application/octet-stream') > -1 ||
          contentType.indexOf('application/zip') > -1)
      ) {
        const fileName = parseFileNameFromDisposition(xhr.getResponseHeader('Content-Disposition')) || defaultFileName;
        const blobUrl = window.URL.createObjectURL(xhr.response);
        const link = document.createElement('a');
        link.style.display = 'none';
        link.href = blobUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        resolve();
        return;
      }

      try {
        const text = await readBlobAsText(xhr.response);
        const res = JSON.parse(text);
        reject(new Error((res && (res.msg || res.message)) || '下载失败'));
      } catch (error) {
        reject(new Error('下载失败'));
      }
    };

    xhr.onerror = () => reject(new Error('网络异常'));
    xhr.send();
  });

export const GetSupplierScSkuFavoriteStats = async (data: any): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/sc/supplier/sku/favorite/stats',
    data,
  });
};

export const GetSupplierScSkuFavoritePage = async (data: any): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/sc/supplier/sku/favorite/page',
    data,
  });
};

export const ExportSupplierScSkuFavorite = (data: any = {}) => {
  const params = new URLSearchParams();
  appendFavoriteExportParams(params, data);
  return downloadFavoriteExport(
    `/portal/sc/supplier/sku/favorite/export${params.toString() ? `?${params.toString()}` : ''}`,
    'sku-favorite-supplier.xls'
  );
};

export const GetAdminScSkuFavoriteStats = async (data: any): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/sc/admin/sku/favorite/stats',
    data,
  });
};

export const GetAdminScSkuFavoritePage = async (data: any): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/sc/admin/sku/favorite/page',
    data,
  });
};

export const ExportAdminScSkuFavorite = (data: any = {}) => {
  const params = new URLSearchParams();
  appendFavoriteExportParams(params, data);
  return downloadFavoriteExport(
    `/portal/sc/admin/sku/favorite/export${params.toString() ? `?${params.toString()}` : ''}`,
    'sku-favorite-admin.xls'
  );
};

export const CreateCurrentScSupplierSku = async (data: any): Promise<any | null> => {
  return await Client.post<any>({
    url: apiPrefix + '/sc/supplier/sku/create',
    data,
  });
};

export const ApplyCurrentScSupplierSkuJd = async (): Promise<any | null> => {
  return await Client.post<any>({
    url: apiPrefix + '/sc/supplier/sku/jd/apply',
  });
};

export const DeleteCurrentScSupplierSku = async (data: any): Promise<any | null> => {
  return await Client.delete<any>({
    url: apiPrefix + '/sc/supplier/sku/delete',
    data,
  });
};
